import {Divider, Typography} from '@mui/material';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {AppArrowRotationIconButton} from 'components/buttons';
import {Avatar} from 'components/Theme';
import {useAppDrawerInRedux, useSprintsRedux} from 'hooks';
import {useSessionRedux} from 'hooks/auth/useSession';
import {useGetAllTenantsByUserId, useUpdateTenantUserIsDefault} from 'hooks/useTenant';
import {TenantModel} from 'models/tenant.model';
import {useItemDataState} from 'providers';
import {MouseEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router';
import {appRoutes} from 'routes';
import {sessionSelector} from 'store/auth/sessionSlice';
import {TenantSetting} from 'views/settings';

export const UserList = () => {
    const {clearSessionInRedux: clearSession, updateDefaultTenant} = useSessionRedux();
    const {tenantId, userId, firstName, lastName} = useSelector(sessionSelector);
    const navigate = useNavigate();
    const {setSelectedSprintInRedux} = useSprintsRedux();
    const {isDrawerOpened} = useAppDrawerInRedux();
    const {clearBoardItemData} = useItemDataState();

    const handleLogout = async () => {
        try {
            clearSession();
            sessionStorage.clear();
            navigate(`/login`, {replace: true});
        } catch (err) {
            console.error(err);
        }
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLLIElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const {getAllTenants, allTenantsLoading, allTenantsResponse} = useGetAllTenantsByUserId();

    const {updateUsersDefaultTenant, updateUserDefaultTenantLoading, updateUserDefaultTenantResponse} =
        useUpdateTenantUserIsDefault();

    useEffect(() => {
        if (updateUserDefaultTenantResponse && !updateUserDefaultTenantLoading) {
            updateDefaultTenant(updateUserDefaultTenantResponse);
            const updatedTenant = allTenantsResponse.find(
                x => x.id === updateUserDefaultTenantResponse.updatedTanentId,
            )?.title;
            if (updatedTenant) {
                navigate(
                    appRoutes.workspace.boardListing(
                        updateUserDefaultTenantResponse.updatedTanentSlug,
                        updateUserDefaultTenantResponse.workspaceSlug,
                        updateUserDefaultTenantResponse.boardSlug,
                    ),
                );
            }
        }
    }, [updateUserDefaultTenantResponse, updateUserDefaultTenantLoading]);

    const handleTenantUpdate = useCallback(
        (tenant: TenantModel) => {
            clearBoardItemData();
            setSelectedSprintInRedux(null);
            updateUsersDefaultTenant({
                variables: {
                    request: {
                        tenantId: tenant.id,
                        tenantSlug: tenant.slug,
                        userId: userId,
                    },
                },
            });
            handleClose();
        },
        [tenantId],
    );

    useEffect(() => {
        if (tenantId && allTenantsResponse.length > 0) {
            const selectedTenant = allTenantsResponse.find(x => x.id === tenantId);
            if (tenantId && selectedTenant?.id && selectedTenant.id !== tenantId) {
                updateUsersDefaultTenant({
                    variables: {
                        request: {
                            tenantId: selectedTenant.id,
                            tenantSlug: selectedTenant.slug,
                            userId: userId,
                        },
                    },
                });
            }
        }
    }, [tenantId]);

    useEffect(() => {
        getAllTenants();
    }, []);

    useEffect(() => {
        if (!allTenantsLoading && allTenantsResponse) {
            const selectedTenant = allTenantsResponse.find(x => x.id === tenantId);
            if (tenantId && selectedTenant?.id && selectedTenant.id !== tenantId) {
                updateUsersDefaultTenant({
                    variables: {
                        request: {
                            tenantId: selectedTenant.id,
                            tenantSlug: selectedTenant.slug,
                            userId: userId,
                        },
                    },
                });
            }
        }
    }, [allTenantsResponse, allTenantsLoading]);

    const selectedTenant = useMemo((): string | null => {
        return allTenantsResponse.find(x => x.id === tenantId)?.title ?? null;
    }, [allTenantsResponse, tenantId]);

    return (
        <Box sx={{p: 1, paddingRight: '15px', borderTop: '2px solid ', borderTopColor: 'divider'}}>
            <ListItem disablePadding onClick={handleClick}>
                <ListItemAvatar style={{paddingLeft: isDrawerOpened ? '0px' : '3px'}}>
                    <Avatar alt="Avatar" />
                </ListItemAvatar>
                {isDrawerOpened && (
                    <Box flexGrow={1} display="flex" justifyContent={'space-between'}>
                        <Box>
                            <Typography>
                                {`${firstName} ${lastName}`.length > 15
                                    ? `${firstName} ${lastName}`.slice(0, 15) + '...'
                                    : `${firstName} ${lastName}`}
                            </Typography>
                            <Typography variant="body2">{selectedTenant && selectedTenant}</Typography>
                        </Box>

                        <AppArrowRotationIconButton isSelected={open} />
                    </Box>
                )}
            </ListItem>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                sx={{
                    marginTop: -2,
                    marginLeft: -2,
                }}
                elevation={4}
                BackdropProps={{
                    style: {
                        backgroundColor: 'transparent', // Makes the backdrop transparent
                    },
                }}
                onClose={handleClose}
                MenuListProps={{'aria-labelledby': 'basic-button', sx: {p: 0}}}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                transformOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Box p={1} minWidth={'240px'}>
                    {allTenantsResponse.length > 1 && (
                        <>
                            <Typography variant="subtitle2">Tenants</Typography>
                            <Divider style={{margin: 0}} />
                            <Box display={'flex'} alignItems={'center'} p={1}>
                                <Typography variant="h6">{selectedTenant && selectedTenant}</Typography>
                                <Typography variant="subtitle2" ml={0.3} fontSize={'10px'}>
                                    (Current)
                                </Typography>
                            </Box>
                            <Divider />
                        </>
                    )}
                    {!allTenantsLoading &&
                        allTenantsResponse.map(tenant =>
                            tenant.id !== tenantId ? (
                                <MenuItem sx={{px: 1}} key={tenant.id} onClick={() => handleTenantUpdate(tenant)}>
                                    {tenant.title}
                                </MenuItem>
                            ) : null,
                        )}
                    <Divider style={{margin: 0}} />
                    <TenantSetting />

                    <MenuItem sx={{px: 1}} onClick={handleLogout}>
                        Logout
                    </MenuItem>
                </Box>
            </Menu>
        </Box>
    );
};
export default UserList;
